import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Send, Bot, User, FileText, Sparkles, Image, X, Upload } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import ConfirmProposalCard from '../components/ConfirmProposalCard';
import { brandName, brandEmail, brandPhone, brandLogo, brandWebsite } from '../lib/brand';

// Simple markdown-like renderer for AI responses
const renderMessage = (content, navigate) => {
  if (!content) return null;
  
  // Split content into parts for different rendering
  const parts = [];
  let remaining = content;
  
  // Handle proposal links [View Proposal](/proposal/xxx)
  const linkRegex = /\[([^\]]+)\]\(\/proposal\/([^)]+)\)/g;
  let lastIndex = 0;
  let match;
  
  while ((match = linkRegex.exec(content)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: content.slice(lastIndex, match.index) });
    }
    // Add the link
    parts.push({ type: 'proposalLink', text: match[1], id: match[2] });
    lastIndex = linkRegex.lastIndex;
  }
  
  // Add remaining text
  if (lastIndex < content.length) {
    parts.push({ type: 'text', content: content.slice(lastIndex) });
  }
  
  return parts.map((part, i) => {
    if (part.type === 'proposalLink') {
      return (
        <Button
          key={i}
          variant="outline"
          size="sm"
          onClick={() => navigate(`/proposal/${part.id}`)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            margin: '4px 0',
            background: '#10b981',
            color: 'white',
            border: 'none'
          }}
        >
          <FileText size={14} /> {part.text}
        </Button>
      );
    }
    
    // Render text with line breaks and basic formatting
    return (
      <span key={i} style={{ whiteSpace: 'pre-wrap' }}>
        {part.content.split('\n').map((line, j) => {
          // Check for success indicator
          if (line.includes('✅')) {
            return (
              <div key={j} style={{ 
                background: '#d1fae5', 
                padding: '8px 12px', 
                borderRadius: '8px', 
                margin: '8px 0',
                fontWeight: '600',
                color: '#065f46'
              }}>
                {line}
              </div>
            );
          }
          // Check for error indicator
          if (line.includes('❌')) {
            return (
              <div key={j} style={{ 
                background: '#fee2e2', 
                padding: '8px 12px', 
                borderRadius: '8px', 
                margin: '8px 0',
                color: '#991b1b'
              }}>
                {line}
              </div>
            );
          }
          // Check for bold text **text**
          if (line.includes('**')) {
            const boldParts = line.split(/\*\*([^*]+)\*\*/g);
            return (
              <React.Fragment key={j}>
                {boldParts.map((p, k) => 
                  k % 2 === 1 ? <strong key={k}>{p}</strong> : p
                )}
                {j < part.content.split('\n').length - 1 && <br />}
              </React.Fragment>
            );
          }
          // Check for bullet points
          if (line.trim().startsWith('- ')) {
            return (
              <div key={j} style={{ paddingLeft: '16px', margin: '4px 0' }}>
                • {line.trim().substring(2)}
              </div>
            );
          }
          return (
            <React.Fragment key={j}>
              {line}
              {j < part.content.split('\n').length - 1 && <br />}
            </React.Fragment>
          );
        })}
      </span>
    );
  });
};

const AIAssistant = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editProposalNumber = searchParams.get('edit');
  const prefillFromUrl = searchParams.get('prefill');
  const messagesEndRef = useRef(null);
  const [messages, setMessages] = useState([
    editProposalNumber ? {
      role: 'assistant',
      content: `Ready to edit **Proposal #${editProposalNumber}**.

Just tell me what to change — examples:
- "change the quantity to 100"
- "add 25 navy 3001 shirts, 1-color front"
- "remove the second item"
- "change M to 30 and L to 25"
- "swap to red G500"

I'll preview the new totals first, then save when you confirm.`,
    } : {
      role: 'assistant',
      content: `Hello! I'm the ${brandName()} AI Assistant. I can help you with:

**💰 Pricing Quotes** - Get instant pricing for any job
**📋 Create Proposals** - I'll create proposals automatically!
**❓ Questions** - Ask about our services, products, or processes

**Quick Start:**
- "Quote 50 black G500 shirts with 2 color print"
- "Create a proposal for John at ABC Corp - 100 navy 3001 shirts, 3 color screen print"

How can I help you today?`,
    },
  ]);
  const [input, setInput] = useState(
    prefillFromUrl
      ? prefillFromUrl
      : (editProposalNumber ? `on ${editProposalNumber} ` : '')
  );
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => Math.random().toString(36).substring(7));
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  // Sticky CPP toggle — when ON, every item the AI creates in this conversation
  // is forced to is_cpp=true (Customer Provided Product). Useful when the
  // admin types something the prompt's CPP heuristics wouldn't catch
  // ("decoration only job", "BYO shirts", etc.).
  const [cppMode, setCppMode] = useState(false);
  const fileInputRef = useRef(null);

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Maximum size is 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      setUploadedImage(base64);
      setImagePreview(reader.result);
      toast.success('Image uploaded! Now tell me what you want to create.');
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() && !uploadedImage) return;

    const userMessage = input;
    const userImage = uploadedImage;
    setInput('');
    
    // Add user message with image preview if uploaded
    setMessages(prev => [...prev, { 
      role: 'user', 
      content: userMessage || (userImage ? '📷 [Image uploaded] Create a proposal with this design' : ''),
      image: imagePreview
    }]);
    
    // Clear image after sending
    setUploadedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    setLoading(true);

    try {
      const response = await axios.post('/ai/chat', {
        message: userMessage || 'I uploaded an image for a proposal. Please help me create a proposal with this design.',
        session_id: sessionId,
        image_base64: userImage || null,
        cpp_mode: cppMode,
      });

      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: response.data.response,
          proposal: response.data.proposal,
          pendingProposal: response.data.pending_proposal || null,
        }
      ]);

      // Auto-open the newly created proposal in a new tab so the admin can
      // review it without losing their chat context. (window.open returns null
      // when a popup blocker stops it — we silently fall back to the inline
      // pill that the chat message will already render.)
      if (response.data.proposal?.success && response.data.proposal?.proposal_id) {
        try {
          window.open(
            `/proposal/${response.data.proposal.proposal_id}`,
            '_blank',
            'noopener',
          );
        } catch { /* ignore — popup blocker */ }
        toast.success(`Proposal ${response.data.proposal.proposal_number} created — opened in new tab`);
      }
    } catch (error) {
      toast.error('Failed to get response');
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleProposalCreated = (result) => {
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `✅ **Proposal Created!**\n- **Proposal #:** ${result.proposal_number}\n- **Customer:** ${result.customer?.name || ''}\n- **Total:** $${Number(result.totals?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}\n\nOpened in a new tab for review.`,
      proposal: result,
    }]);
    try { window.open(`/proposal/${result.proposal_id}`, '_blank', 'noopener'); } catch { /* popup blocker */ }
    toast.success(`Proposal ${result.proposal_number} created`);
  };

  const quickPrompts = [
    "Quote 50 black G500 shirts with 2 color print",
    "What's the price for embroidery on 25 polos?",
    "Create a proposal for a new customer"
  ];

  return (
    <div className="dashboard" data-testid="ai-assistant">
      <AppHeader />

      <main className="dashboard-content">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Sparkles size={24} color="white" />
          </div>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0 }} data-testid="page-title">
              AI Assistant
            </h1>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
              Get quotes & create proposals instantly
            </p>
          </div>
        </div>

        <div className="ai-chat-container" style={{ 
          background: 'white', 
          borderRadius: '16px', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
          maxWidth: '900px', 
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 220px)',
          minHeight: '500px'
        }}>
          {/* Quick Prompts */}
          <div style={{ 
            padding: '12px 24px', 
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '12px', color: '#9ca3af', alignSelf: 'center' }}>Try:</span>
            {quickPrompts.map((prompt, idx) => (
              <button
                key={idx}
                onClick={() => setInput(prompt)}
                style={{
                  padding: '4px 12px',
                  borderRadius: '16px',
                  border: '1px solid #e5e7eb',
                  background: '#f9fafb',
                  color: '#374151',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
                onMouseLeave={(e) => e.target.style.background = '#f9fafb'}
              >
                {prompt}
              </button>
            ))}
          </div>

          {/* Chat Messages */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}
            data-testid="chat-messages"
          >
            {messages.map((message, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'flex-start',
                  flexDirection: message.role === 'user' ? 'row-reverse' : 'row'
                }}
                data-testid={`message-${idx}`}
              >
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: message.role === 'user' 
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    flexShrink: 0
                  }}
                >
                  {message.role === 'user' ? <User size={20} /> : <Bot size={20} />}
                </div>
                <div
                  style={{
                    background: message.role === 'user' 
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                      : '#f7fafc',
                    color: message.role === 'user' ? 'white' : '#2d3748',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    maxWidth: '75%',
                    wordWrap: 'break-word',
                    boxShadow: message.role === 'assistant' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                  }}
                  data-testid={`message-content-${idx}`}
                >
                  {/* Show image if present */}
                  {message.image && (
                    <div style={{ 
                      marginBottom: '8px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      maxWidth: '200px'
                    }}>
                      <img 
                        src={message.image} 
                        alt="Uploaded design" 
                        style={{ width: '100%', height: 'auto', display: 'block' }}
                      />
                    </div>
                  )}
                  {message.role === 'user'
                    ? message.content
                    : renderMessage(message.content, navigate)
                  }
                  {message.pendingProposal && (
                    <ConfirmProposalCard
                      pending={message.pendingProposal}
                      sessionId={sessionId}
                      onCreated={handleProposalCreated}
                    />
                  )}
                  {message.proposal?.success && message.proposal?.proposal_id && (
                    <div style={{ marginTop: 10 }}>
                      <a
                        href={`/proposal/${message.proposal.proposal_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`open-proposal-pill-${idx}`}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          background: '#10b981', color: 'white',
                          padding: '6px 12px', borderRadius: 9999,
                          fontSize: 12, fontWeight: 700, textDecoration: 'none',
                        }}
                      >
                        Open Proposal {message.proposal.proposal_number} ↗
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }} data-testid="loading-message">
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white'
                  }}
                >
                  <Bot size={20} />
                </div>
                <div style={{ 
                  background: '#f7fafc', 
                  padding: '12px 16px', 
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Image Preview */}
          {imagePreview && (
            <div style={{
              padding: '12px 24px',
              borderTop: '1px solid #e2e8f0',
              background: '#f0f9ff',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '2px solid #3b82f6',
                flexShrink: 0
              }}>
                <img 
                  src={imagePreview} 
                  alt="Upload preview" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: '500', color: '#1e40af', margin: 0 }}>📷 Design/Logo Ready</p>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                  Tell me: customer name, shirt style, color, quantity, and print locations
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={removeImage}
                style={{ color: '#ef4444' }}
              >
                <X size={18} />
              </Button>
            </div>
          )}

          {/* Input Area */}
          <form
            onSubmit={sendMessage}
            className="ai-input-form"
            style={{
              borderTop: '1px solid #e2e8f0',
              padding: '16px 24px',
              display: 'flex',
              gap: '12px',
              background: '#fafafa',
              borderRadius: '0 0 16px 16px'
            }}
            data-testid="chat-input-form"
          >
            {/* Hidden file input */}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
            
            {/* Upload Image Button */}
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              style={{ 
                minWidth: '48px',
                background: imagePreview ? '#dbeafe' : 'white',
                borderColor: imagePreview ? '#3b82f6' : '#e2e8f0'
              }}
              title="Upload design/logo"
            >
              <Image size={18} className={imagePreview ? 'text-blue-600' : ''} />
            </Button>

            {/* CPP toggle — sticky for the whole conversation. When ON, the
                backend forces is_cpp=true on every item the AI proposes. */}
            <button
              type="button"
              onClick={() => setCppMode((v) => !v)}
              disabled={loading}
              data-testid="cpp-mode-toggle"
              aria-pressed={cppMode}
              title={
                cppMode
                  ? 'Customer-supplied mode is ON — every item will be flagged is_cpp:true (no garment price). Click to turn off.'
                  : 'Click to mark every item in this chat as Customer Provided (no garment price)'
              }
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '0 14px',
                borderRadius: 999,
                border: `1px solid ${cppMode ? '#f59e0b' : '#e2e8f0'}`,
                background: cppMode ? '#fef3c7' : '#fff',
                color: cppMode ? '#92400e' : '#475569',
                fontSize: 12,
                fontWeight: 700,
                whiteSpace: 'nowrap',
                cursor: loading ? 'not-allowed' : 'pointer',
                letterSpacing: 0.3,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: cppMode ? '#d97706' : '#cbd5e1',
                  display: 'inline-block',
                }}
              />
              {cppMode ? 'CUSTOMER SUPPLIED · ON' : 'Customer Supplied'}
            </button>
            
            {/* Multi-line composer — Enter inserts a new line so the user can
                organise their request before pressing Send. Cmd/Ctrl+Enter
                submits as a power-user shortcut. */}
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  sendMessage(e);
                }
              }}
              placeholder={imagePreview
                ? "Describe your order (customer, qty, shirt, colors)…\n\nTip: Enter = new line · Send button = submit"
                : "Ask for a quote or create a proposal…\n\nTip: Enter = new line · Send button = submit (Ctrl/⌘+Enter also works)"}
              disabled={loading}
              data-testid="chat-input"
              rows={3}
              style={{
                flex: 1,
                padding: '10px 12px',
                fontSize: '14px',
                lineHeight: 1.5,
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontFamily: 'inherit',
                resize: 'vertical',
                minHeight: 48,
                maxHeight: 220,
              }}
            />
            <Button 
              type="submit" 
              disabled={loading || (!input.trim() && !uploadedImage)} 
              data-testid="send-message-button"
              style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                minWidth: '48px'
              }}
            >
              <Send size={18} />
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AIAssistant;
